import * as React from "react";
import { SelectableButton } from "../../core/core-widgets/buttons/SelectableButton";
import { EditFeatureOptions, EditFeatureState } from "../redux";
import { GlyphIcon, Tooltip, TooltipPosition } from "../../core/core-widgets";
import { FeatureCollection, Features, GeometryType } from "../../core/geo/types";
import { EditAction } from "../redux/types";

enum EditFeatureAction {
    DENSIFY = "densify",
    ADD_VERTEX = "add_vertex",
    REMOVE_VERTEX = "remove_vertex",
    MOVE = "move",
    ARC_BY_EDGE = "arc_by_edge",
    ARC_BY_ANGLES = "arc_by_angles",
    FOLLOW_BORDER = "follow_border",
    GREAT_CIRCLE = "great_circle",
    FINISH = "finish",
    CANCEL_EDITING = "cancel",
}

type EditFeatureActionDescriptor = {
    [key in EditFeatureAction]: {
        id: string;
        onClick: () => void;
        glyph?: GlyphIcon;
        isSelected?: boolean;
        tooltipText: string;
        disabled: boolean;
        hidden?: boolean;
        imagePath?: string;
    };
};

export interface EditFeatureStateProps {
    editEnabled: boolean;
    editFeature: EditFeatureState;
    editAction: EditAction;
    editPaused: boolean;
    arcModeEnabled: boolean;
}

export interface EditFeatureDispatchProps {
    finish: () => void;
    densifyGeometry: () => void;
    changeEditActionMode: (value: EditAction) => void;
    selectFeatureForEditing: (features: FeatureCollection, options: EditFeatureOptions) => void;
    cancelEditing: () => void;
}

export const EditFeatureToolbar = (props: EditFeatureStateProps & EditFeatureDispatchProps) => {
    const {
        editEnabled,
        editFeature,
        editAction,
        densifyGeometry,
        changeEditActionMode,
        finish,
        cancelEditing,
        editPaused,
        arcModeEnabled,
    } = props;

    if (!editEnabled) {
        return null;
    }

    const isLineOrPolygon =
        editFeature.geometryType === GeometryType.LINE_STRING || editFeature.geometryType === GeometryType.POLYGON;

    const handleAddVertex = () => {
        const action = editAction === EditAction.ADD_VERTEX ? EditAction.NONE : EditAction.ADD_VERTEX;
        changeEditActionMode(action);
    };

    const handleRemoveVertex = () => {
        const action = editAction === EditAction.REMOVE_VERTEX ? EditAction.NONE : EditAction.REMOVE_VERTEX;
        changeEditActionMode(action);
    };

    const handleMove = () => {
        const action = editAction === EditAction.MOVE_FEATURE ? EditAction.NONE : EditAction.MOVE_FEATURE;
        changeEditActionMode(action);
    };

    const handleFollowGeoBorder = () => {
        const action = editAction === EditAction.FOLLOW_GEO_BORDER ? EditAction.NONE : EditAction.FOLLOW_GEO_BORDER;
        changeEditActionMode(action);
    };

    const handleToGreatCircle = () => {
        const action = editAction === EditAction.TO_GREAT_CIRCLE ? EditAction.NONE : EditAction.TO_GREAT_CIRCLE;
        changeEditActionMode(action);
    };

    const handleArcByAngles = () => {
        const action = editAction === EditAction.ARC_BY_ANGLES ? EditAction.NONE : EditAction.ARC_BY_ANGLES;
        changeEditActionMode(action);
    };

    const handleArcByEdge = () => {
        const action = editAction === EditAction.ARC_BY_EDGE ? EditAction.NONE : EditAction.ARC_BY_EDGE;
        changeEditActionMode(action);
    };

    const ICONS_FOLDER = "../../../resources/icons";

    const actions: EditFeatureActionDescriptor = {
        [EditFeatureAction.DENSIFY]: {
            id: "densifyGeometryButton",
            onClick: densifyGeometry,
            imagePath: `${ICONS_FOLDER}/densify_geo.svg`,
            isSelected: editFeature.options?.densifyGeometry,
            tooltipText: "Densify geometry",
            disabled: !isLineOrPolygon || editPaused,
            hidden: editFeature.newFeature,
        },
        [EditFeatureAction.ADD_VERTEX]: {
            id: "addVertexButton",
            onClick: handleAddVertex,
            imagePath: `${ICONS_FOLDER}/add_vertex.svg`,
            isSelected: editFeature.options?.action === EditAction.ADD_VERTEX,
            tooltipText: "Add vertex",
            disabled: !isLineOrPolygon || editPaused,
            hidden: editFeature.newFeature,
        },
        [EditFeatureAction.REMOVE_VERTEX]: {
            id: "removeVertexButton",
            onClick: handleRemoveVertex,
            imagePath: `${ICONS_FOLDER}/remove_vertex.svg`,
            isSelected: editFeature.options?.action === EditAction.REMOVE_VERTEX,
            tooltipText: "Remove vertex",
            disabled: !isLineOrPolygon || editPaused,
            hidden: editFeature.newFeature,
        },
        [EditFeatureAction.MOVE]: {
            id: "moveFeatureButton",
            onClick: handleMove,
            isSelected: editFeature.options?.action === EditAction.MOVE_FEATURE,
            tooltipText: "Move Feature",
            disabled: !isLineOrPolygon || editPaused,
            hidden: true, //move action hidden until further notice, it may be used in future for AMDB data editing
        },
        [EditFeatureAction.FINISH]: {
            id: "finishVisualEditingButton",
            onClick: finish,
            imagePath: `${ICONS_FOLDER}/visual_edit.svg`,
            tooltipText: "Finish visual editing",
            disabled: editFeature.newFeature! || editPaused,
            hidden: editFeature.newFeature,
        },
        [EditFeatureAction.ARC_BY_EDGE]: {
            id: "arcByEdgeButton",
            onClick: handleArcByEdge,
            imagePath: `${ICONS_FOLDER}/arc_by_edge.svg`,
            tooltipText: "Arc by edge",
            isSelected: editFeature.options?.action === EditAction.ARC_BY_EDGE,
            disabled: !isLineOrPolygon || editPaused,
            hidden: arcModeEnabled || editFeature.newFeature,
        },
        [EditFeatureAction.ARC_BY_ANGLES]: {
            id: "arcByCenterButton",
            onClick: handleArcByAngles,
            imagePath: `${ICONS_FOLDER}/arc_by_center.svg`,
            tooltipText: "Arc by center",
            isSelected: editFeature.options?.action === EditAction.ARC_BY_ANGLES,
            disabled: !isLineOrPolygon || editPaused,
            hidden: arcModeEnabled || editFeature.newFeature,
        },
        [EditFeatureAction.FOLLOW_BORDER]: {
            id: "followBorderButton",
            onClick: handleFollowGeoBorder,
            imagePath: `${ICONS_FOLDER}/follow_geoborder.svg`,
            isSelected: editFeature.options?.action === EditAction.FOLLOW_GEO_BORDER,
            tooltipText: "Follow geoBorder",
            disabled:
                editFeature.geometryType !== GeometryType.POLYGON ||
                editFeature.editedFeatures?.features[0].properties.featureType !== Features.AIRSPACE ||
                editPaused,
            hidden: editFeature.newFeature,
        },
        [EditFeatureAction.GREAT_CIRCLE]: {
            id: "toGreatCircleButton",
            onClick: handleToGreatCircle,
            imagePath: `${ICONS_FOLDER}/to_grc.svg`,
            isSelected: editFeature.options?.action === EditAction.TO_GREAT_CIRCLE,
            tooltipText: "To great circle",
            disabled: !isLineOrPolygon || editPaused,
            hidden: editFeature.newFeature,
        },
        [EditFeatureAction.CANCEL_EDITING]: {
            id: "cancelEditingButton",
            onClick: () => cancelEditing(),
            glyph: GlyphIcon.CLOSE,
            tooltipText: "Cancel Editing",
            disabled: false,
            hidden: !editFeature.newFeature,
        },
    };

    return (
        <div className={"editToolbar"}>
            {Object.values(EditFeatureAction)
                .filter((action) => !actions[action].hidden)
                .map((action) => {
                    const config = actions[action];
                    return (
                        <Tooltip body={config.tooltipText} position={TooltipPosition.BOTTOM}>
                            <SelectableButton
                                id={config.id}
                                onClick={config.onClick}
                                glyph={config.glyph}
                                isSelected={config.isSelected}
                                disabled={config.disabled}
                                className={"toolbarItem"}
                                imagePath={config.imagePath}
                            />
                        </Tooltip>
                    );
                })}
        </div>
    );
};
